import React, { useCallback } from 'react';
import { useNavigate } from 'react-router';
import LoginForm from 'Components/AuthForm/LoginForm';
import UIModal from 'Components/UIComponents/UIModal';
import { usePrimarySignIn } from 'Hooks/Auth';
import { UnauthorizedRoutePaths } from 'Interfaces/RoutePaths';
import { DataLayerAnalytics } from 'Services/Integrations';
import Toast from 'Services/Toast';
import { isNotEmpty } from 'Utils/functions';
import {
  isEmailConfirmationData,
  isTwoFactorResponseData,
  isUserResponseData,
} from 'Utils/typeGuards';

import ModalHeader from '../ModalHeader';

interface LoginModalProps {
  onClose: () => void;
  onSignUpClick: () => void;
}

const LoginModal = ({ onClose, onSignUpClick }: LoginModalProps) => {
  const navigate = useNavigate();
  const [callSignIn, isLoading] = usePrimarySignIn();

  const handleSignIn = useCallback(
    async values => {
      try {
        const response = await callSignIn(values);

        if (!isNotEmpty(response)) {
          return;
        }

        if (isTwoFactorResponseData(response)) {
          navigate(UnauthorizedRoutePaths.TWO_FACTOR);
        }

        if (isEmailConfirmationData(response)) {
          navigate(UnauthorizedRoutePaths.CONFIRM_ACCOUNT, {
            state: { confirmationRequired: true },
          });
        }

        if (isUserResponseData(response) && response.isNewUser) {
          DataLayerAnalytics.fireGoogleRegistrationEvent();
          Toast.success('Your account has been created.');
        }
      } catch (error) {
        Toast.error(error.message);
      }
    },
    [callSignIn, navigate],
  );

  return (
    <UIModal
      onClose={onClose}
      overlayClassName="successSignModal__overlay"
      className="successSignModal"
      hideCloseIcon
    >
      <div className="successSignModal__wrapper">
        <div className="successSignModal__header-wrapper">
          <ModalHeader />
        </div>
        <div className="successSignModal__subtitle">Sign in to your account</div>
        <LoginForm
          onSignUpClick={onSignUpClick}
          formClassName="successSignModal__auth"
          onSubmit={handleSignIn}
          isLoading={isLoading}
        />
      </div>
    </UIModal>
  );
};

export default LoginModal;
